import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Calendar, Clock, MessageSquare, Send, CheckCircle, ArrowRight, Building2, Briefcase, Users, User, Phone } from 'lucide-react';
import Button from './ui/Button';
import { supabase } from '../lib/supabase';

const formVariants = {
  hidden: { 
    opacity: 0,
    y: 20,
    scale: 0.95
  },
  visible: { 
    opacity: 1,
    y: 0,
    scale: 1,
    transition: {
      type: "spring",
      stiffness: 300,
      damping: 30,
      mass: 0.8,
      staggerChildren: 0.1
    }
  }
};

const stepVariants = {
  hidden: { 
    opacity: 0,
    x: -20
  },
  visible: { 
    opacity: 1,
    x: 0,
    transition: {
      type: "spring",
      stiffness: 300,
      damping: 30
    }
  }
};

const successVariants = {
  hidden: { 
    scale: 0,
    opacity: 0
  },
  visible: { 
    scale: 1,
    opacity: 1,
    transition: {
      type: "spring",
      stiffness: 200,
      damping: 20
    }
  }
};

export default function ConsultationForm() {
  const [formData, setFormData] = useState({
    name: '',
    phoneNumber: '',
    businessType: '',
    industry: '',
    companySize: '',
    date: '',
    time: '',
    message: '',
  });
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [currentStep, setCurrentStep] = useState(1);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    try {
      // Validate form data
      if (!formData.name || !formData.phoneNumber) {
        throw new Error('Please provide your contact information.');
      }
      if (!formData.businessType || !formData.industry || !formData.companySize) {
        throw new Error('Please provide your business details.');
      }
      if (!formData.date || !formData.time) {
        throw new Error('Please select your preferred consultation time.');
      }
      if (!formData.message) {
        throw new Error('Please describe your project.');
      }

      // Get current user if logged in
      const { data: { user } } = await supabase.auth.getUser();
      
      // Insert consultation
      const { error: insertError } = await supabase
        .from('consultations')
        .insert({
          user_id: user?.id || null,
          date: formData.date,
          time: formData.time,
          message: formData.message,
          status: 'pending',
          business_details: {
            type: formData.businessType,
            industry: formData.industry,
            size: formData.companySize,
            name: formData.name,
            phoneNumber: formData.phoneNumber
          }
        });

      if (insertError) {
        throw new Error(insertError.message);
      }

      // Create notification for logged-in users
      if (user) {
        try {
          await supabase.rpc('create_notification', {
            p_user_id: user.id,
            p_title: 'Consultation Booked',
            p_message: `Your consultation has been scheduled for ${formData.date} at ${formData.time}`,
            p_type: 'success'
          });
        } catch (notificationError) {
          console.warn('Failed to create notification:', notificationError);
          // Don't throw error for notification - non-critical feature
        }
      }

      setSuccess(true);
      setFormData({
        name: '',
        phoneNumber: '',
        businessType: '',
        industry: '',
        companySize: '',
        date: '',
        time: '',
        message: ''
      });
    } catch (error) {
      console.error('Error booking consultation:', error);
      setError(error instanceof Error ? error.message : 'Failed to book consultation');
    } finally {
      setLoading(false);
    }
  };

  const nextStep = () => {
    setCurrentStep(prev => Math.min(prev + 1, 4));
  };

  const prevStep = () => {
    setCurrentStep(prev => Math.max(prev - 1, 1));
  };

  if (success) {
    return (
      <motion.div
        variants={successVariants}
        initial="hidden"
        animate="visible"
        className="bg-white rounded-2xl shadow-xl p-6 sm:p-8 max-w-md mx-auto text-center"
      >
        <motion.div
          initial={{ scale: 0 }}
          animate={{ scale: 1 }}
          transition={{ delay: 0.2 }}
          className="w-16 h-16 sm:w-20 sm:h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6"
        >
          <CheckCircle className="w-8 h-8 sm:w-10 sm:h-10 text-green-500" />
        </motion.div>
        <h3 className="text-xl sm:text-2xl font-bold text-gray-900 mb-4">Consultation Request Sent!</h3>
        <p className="text-gray-600 mb-8">
          We'll contact you within 24 hours to confirm your appointment.
          Our team is excited to discuss your project and help you achieve your goals!
        </p>
        <Button
          onClick={() => {
            setSuccess(false);
            setCurrentStep(1);
          }}
          className="w-full inline-flex items-center justify-center group"
        >
          Book Another Consultation
          <ArrowRight className="ml-2 h-4 w-4 transition-transform group-hover:translate-x-1" />
        </Button>
      </motion.div>
    );
  }

  return (
    <motion.div
      variants={formVariants}
      initial="hidden"
      animate="visible"
      className="bg-white rounded-2xl shadow-xl p-4 sm:p-8 max-w-2xl mx-auto"
    >
      {error && (
        <motion.div
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-6 bg-red-50 border border-red-200 text-red-600 px-4 py-3 rounded-lg flex items-center gap-2"
        >
          <span className="w-2 h-2 rounded-full bg-red-600 animate-pulse" />
          {error}
        </motion.div>
      )}

      <div className="flex justify-between items-center mb-8">
        <div className="flex space-x-2">
          {[1, 2, 3, 4].map((step) => (
            <motion.div
              key={step}
              initial={{ scale: 0 }}
              animate={{ 
                scale: 1,
                backgroundColor: step === currentStep ? 'rgb(37, 99, 235)' : step < currentStep ? 'rgb(191, 219, 254)' : 'rgb(229, 231, 235)'
              }}
              transition={{ delay: step * 0.1 }}
              className={`w-2 sm:w-3 h-2 sm:h-3 rounded-full`}
            />
          ))}
        </div>
        <span className="text-sm text-gray-500">Step {currentStep} of 4</span>
      </div>
      
      <form onSubmit={handleSubmit} className="space-y-6">
        <AnimatePresence mode="wait">
          {currentStep === 1 && (
            <motion.div
              key="step1"
              variants={stepVariants}
              initial="hidden"
              animate="visible"
              exit="hidden"
              className="space-y-6"
            >
              <h3 className="text-lg sm:text-xl font-semibold mb-4">Your Contact Information</h3>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Full Name
                </label>
                <div className="relative">
                  <User className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-5 w-5" />
                  <input
                    type="text"
                    required
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    placeholder="Enter your full name"
                    className="pl-10 w-full rounded-lg border border-gray-300 p-3 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Phone Number
                </label>
                <div className="relative">
                  <Phone className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-5 w-5" />
                  <input
                    type="tel"
                    required
                    value={formData.phoneNumber}
                    onChange={(e) => setFormData({ ...formData, phoneNumber: e.target.value })}
                    placeholder="Enter your phone number"
                    className="pl-10 w-full rounded-lg border border-gray-300 p-3 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
              </div>
            </motion.div>
          )}

          {currentStep === 2 && (
            <motion.div
              key="step2"
              variants={stepVariants}
              initial="hidden"
              animate="visible"
              exit="hidden"
              className="space-y-6"
            >
              <h3 className="text-lg sm:text-xl font-semibold mb-4">Tell Us About Your Business</h3>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Business Type
                </label>
                <div className="relative">
                  <Building2 className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-5 w-5" />
                  <select
                    required
                    value={formData.businessType}
                    onChange={(e) => setFormData({ ...formData, businessType: e.target.value })}
                    className="pl-10 w-full rounded-lg border border-gray-300 p-3 focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white"
                  >
                    <option value="">Select business type</option>
                    <option value="startup">Startup</option>
                    <option value="small_business">Small Business</option>
                    <option value="enterprise">Enterprise</option>
                    <option value="freelancer">Freelancer</option>
                  </select>
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Industry
                </label>
                <div className="relative">
                  <Briefcase className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-5 w-5" />
                  <select
                    required
                    value={formData.industry}
                    onChange={(e) => setFormData({ ...formData, industry: e.target.value })}
                    className="pl-10 w-full rounded-lg border border-gray-300 p-3 focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white"
                  >
                    <option value="">Select industry</option>
                    <option value="technology">Technology</option>
                    <option value="healthcare">Healthcare</option>
                    <option value="finance">Finance</option>
                    <option value="education">Education</option>
                    <option value="retail">Retail</option>
                    <option value="other">Other</option>
                  </select>
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Company Size
                </label>
                <div className="relative">
                  <Users className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-5 w-5" />
                  <select
                    required
                    value={formData.companySize}
                    onChange={(e) => setFormData({ ...formData, companySize: e.target.value })}
                    className="pl-10 w-full rounded-lg border border-gray-300 p-3 focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white"
                  >
                    <option value="">Select company size</option>
                    <option value="1-10">1-10 employees</option>
                    <option value="11-50">11-50 employees</option>
                    <option value="51-200">51-200 employees</option>
                    <option value="201-500">201-500 employees</option>
                    <option value="500+">500+ employees</option>
                  </select>
                </div>
              </div>
            </motion.div>
          )}

          {currentStep === 3 && (
            <motion.div
              key="step3"
              variants={stepVariants}
              initial="hidden"
              animate="visible"
              exit="hidden"
              className="space-y-6"
            >
              <h3 className="text-lg sm:text-xl font-semibold mb-4">Schedule Your Consultation</h3>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Preferred Date
                </label>
                <div className="relative">
                  <Calendar className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-5 w-5" />
                  <input
                    type="date"
                    required
                    value={formData.date}
                    onChange={(e) => setFormData({ ...formData, date: e.target.value })}
                    className="pl-10 w-full rounded-lg border border-gray-300 p-3 focus:outline-none focus:ring-2 focus:ring-blue-500"
                    min={new Date().toISOString().split('T')[0]}
                  />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Preferred Time
                </label>
                <div className="relative">
                  <Clock className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-5 w-5" />
                  <input
                    type="time"
                    required
                    value={formData.time}
                    onChange={(e) => setFormData({ ...formData, time: e.target.value })}
                    className="pl-10 w-full rounded-lg border border-gray-300 p-3 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
              </div>
            </motion.div>
          )}

          {currentStep === 4 && (
            <motion.div
              key="step4"
              variants={stepVariants}
              initial="hidden"
              animate="visible"
              exit="hidden"
              className="space-y-6"
            >
              <h3 className="text-lg sm:text-xl font-semibold mb-4">Tell Us About Your Project</h3>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  
                  Project Details
                </label>
                <div className="relative">
                  <MessageSquare className="absolute left-3 top-3 text-gray-400 h-5 w-5" />
                  <textarea
                    required
                    value={formData.message}
                    onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                    className="pl-10 w-full rounded-lg border border-gray-300 p-3 h-32 focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="Tell us about your project goals, challenges, and what you hope to achieve..."
                  />
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        <div className="flex flex-col sm:flex-row justify-between items-center gap-4 pt-6">
          <div className="w-full sm:w-auto order-2 sm:order-1">
            {currentStep > 1 && (
              <Button
                type="button"
                variant="outline"
                onClick={prevStep}
                className="w-full sm:w-32"
              >
                Back
              </Button>
            )}
          </div>
          
          <div className="w-full sm:w-auto order-1 sm:order-2">
            {currentStep < 4 ? (
              <Button
                type="button"
                onClick={nextStep}
                className="w-full sm:w-32 inline-flex items-center justify-center group"
              >
                Next
                <ArrowRight className="w-4 h-4 ml-2 transition-transform group-hover:translate-x-1" />
              </Button>
            ) : (
              <Button
                type="submit"
                className="w-full sm:w-32 inline-flex items-center justify-center group"
                disabled={loading}
              >
                {loading ? (
                  'Sending...'
                ) : (
                  <>
                    Send
                    <Send className="w-4 h-4 ml-2 transition-transform group-hover:translate-x-1" />
                  </>
                )}
              </Button>
            )}
          </div>
        </div>
      </form>
    </motion.div>
  );
}